import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import logo from "../../assets/logo.png";
import L from "../../constants/baysportLabels";
import { LegalFooterLinks } from "../Legal/LegalDocLink";
import { trackTryClick } from "../../analytics/metrikaGoals";
import {
  MARKETING_AUDIENCE,
  MARKETING_FEATURES,
  MARKETING_FOOTER_COLUMNS,
  MARKETING_PATHS,
} from "./marketingNav";
import s from "./marketing.module.css";

export default function MarketingShell({ children, metrikaLanding = "marketing" }) {
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (!navRef.current?.contains(e.target)) setOpenMenu(null);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const onTry = () => trackTryClick({ landing: metrikaLanding });

  return (
    <div className={`${s.shell} ${mobileOpen ? s.mobileOpen : ""}`}>
      <header className={s.nav} ref={navRef}>
        <div className={s.navInner}>
          <Link to="/" className={s.brand} onClick={() => setMobileOpen(false)}>
            <img src={logo} alt="" className={s.brandLogo} />
            <span>{L.productName}</span>
          </Link>

          <nav className={s.navMenus} aria-label="Маркетинг">
            <div
              className={`${s.navItem} ${openMenu === "audience" ? s.navItemOpen : ""}`}
            >
              <button
                type="button"
                className={s.navTrigger}
                aria-expanded={openMenu === "audience"}
                onClick={() =>
                  setOpenMenu((v) => (v === "audience" ? null : "audience"))
                }
              >
                Для кого <i className={s.chevron} aria-hidden />
              </button>
              <div className={s.dropdown}>
                {MARKETING_AUDIENCE.map((col) => (
                  <div key={col.title} className={s.dropCol}>
                    <h4>{col.title}</h4>
                    {col.items.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        className={s.dropLink}
                        onClick={() => setOpenMenu(null)}
                      >
                        <strong>{item.title}</strong>
                        <span>{item.text}</span>
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            <div
              className={`${s.navItem} ${openMenu === "features" ? s.navItemOpen : ""}`}
            >
              <button
                type="button"
                className={s.navTrigger}
                aria-expanded={openMenu === "features"}
                onClick={() =>
                  setOpenMenu((v) => (v === "features" ? null : "features"))
                }
              >
                Возможности <i className={s.chevron} aria-hidden />
              </button>
              <div
                className={s.dropdown}
                style={{
                  width: "min(520px, calc(100vw - 32px))",
                  gridTemplateColumns: "1fr 1fr",
                }}
              >
                {MARKETING_FEATURES.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={s.dropLink}
                    onClick={() => setOpenMenu(null)}
                  >
                    <strong>{item.title}</strong>
                    <span>{item.text}</span>
                  </Link>
                ))}
                <Link
                  to={MARKETING_PATHS.features}
                  className={s.dropLink}
                  onClick={() => setOpenMenu(null)}
                >
                  <strong>Все возможности</strong>
                  <span>Обзор модулей платформы</span>
                </Link>
              </div>
            </div>

            <Link to={MARKETING_PATHS.pricing} className={s.navLink}>
              Тарифы
            </Link>
            <Link to={MARKETING_PATHS.blog} className={s.navLink}>
              Блог
            </Link>
            <Link to={MARKETING_PATHS.about} className={s.navLink}>
              О компании
            </Link>
          </nav>

          <div className={s.navActions}>
            <Link to="/account/login" className={`${s.btn} ${s.btnGhost}`}>
              Войти
            </Link>
            <Link
              to={MARKETING_PATHS.buy}
              className={`${s.btn} ${s.btnPrimary}`}
              onClick={onTry}
            >
              Попробовать
            </Link>
            <button
              type="button"
              className={s.burger}
              aria-label="Меню"
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((v) => !v)}
            >
              <span />
            </button>
          </div>
        </div>

        <div className={s.mobilePanel}>
          <Link
            to={MARKETING_PATHS.forWhom}
            className={s.mobileLink}
            onClick={() => setMobileOpen(false)}
          >
            Для кого
          </Link>
          <div className={s.mobileSub}>
            {MARKETING_AUDIENCE.flatMap((col) => col.items)
              .slice(0, 8)
              .map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                >
                  {item.title}
                </Link>
              ))}
          </div>
          <Link
            to={MARKETING_PATHS.features}
            className={s.mobileLink}
            onClick={() => setMobileOpen(false)}
          >
            Возможности
          </Link>
          <Link
            to={MARKETING_PATHS.pricing}
            className={s.mobileLink}
            onClick={() => setMobileOpen(false)}
          >
            Тарифы
          </Link>
          <Link
            to={MARKETING_PATHS.blog}
            className={s.mobileLink}
            onClick={() => setMobileOpen(false)}
          >
            Блог
          </Link>
          <Link
            to={MARKETING_PATHS.about}
            className={s.mobileLink}
            onClick={() => setMobileOpen(false)}
          >
            О компании
          </Link>
          <Link
            to="/account/login"
            className={s.mobileLink}
            onClick={() => setMobileOpen(false)}
          >
            Войти
          </Link>
        </div>
      </header>

      <main className={s.main}>{children}</main>

      <footer className={s.footer}>
        <div className={s.footerInner}>
          <div>
            <div className={s.footerBrand}>{L.productName}</div>
            <p className={s.footerText}>
              CRM для спортивных клубов и тренеров: журнал, абонементы и кабинет
              родителей под вашим брендом.
            </p>
          </div>
          {MARKETING_FOOTER_COLUMNS.map((col) => (
            <div key={col.title}>
              <h4>{col.title}</h4>
              {col.links.map((link) => (
                <Link key={`${col.title}-${link.to}`} to={link.to}>
                  {link.label}
                </Link>
              ))}
            </div>
          ))}
        </div>
        <div className={s.footerBottom}>
          <span>© {new Date().getFullYear()} {L.productName}</span>
          <LegalFooterLinks />
        </div>
      </footer>
    </div>
  );
}
